/* Tasks Client barrel export */

import {
  IOperationalDaysClient,
  IOperationalOverviewClient,
  ITasksClient,
  ITaskTemplatesClient,
  ITaskCategoriesClient,
  ITaskStatusesClient,
  ITaskPrioritiesClient,
  ITaskSchedulesClient,
  ITaskReportingDashboardClient,
} from './ITasksClient';
import {
  OperationalDaysClientApi,
  OperationalOverviewClientApi,
  TasksClientApi,
  TaskTemplatesClientApi,
  TaskCategoriesClientApi,
  TaskStatusesClientApi,
  TaskPrioritiesClientApi,
  TaskSchedulesClientApi,
  TaskReportingDashboardClientApi,
} from './tasksClientApi';
import {
  OperationalDaysClientMocked,
  OperationalOverviewClientMocked,
  TasksClientMocked,
  TaskTemplatesClientMocked,
  TaskCategoriesClientMocked,
  TaskStatusesClientMocked,
  TaskPrioritiesClientMocked,
  TaskSchedulesClientMocked,
  TaskReportingDashboardClientMocked,
} from './tasksClientMocked';

// Re-export types
export * from './tasksTypes';
export * from './ITasksClient';

/* ---- Configuration ---- */

import { shouldUseMockedClient } from '../../environment';

/**
 * Determines whether to use mocked clients.
 * Automatically uses mocked clients on localhost and ofstest5 environments.
 */
const isMockedEnvironment = () => shouldUseMockedClient();

/* ---- Factory Functions ---- */

let operationalDaysClientInstance: IOperationalDaysClient | null = null;
let operationalOverviewClientInstance: IOperationalOverviewClient | null = null;
let tasksClientInstance: ITasksClient | null = null;
let taskTemplatesClientInstance: ITaskTemplatesClient | null = null;
let taskCategoriesClientInstance: ITaskCategoriesClient | null = null;
let taskStatusesClientInstance: ITaskStatusesClient | null = null;
let taskPrioritiesClientInstance: ITaskPrioritiesClient | null = null;
let taskSchedulesClientInstance: ITaskSchedulesClient | null = null;
let taskReportingDashboardClientInstance: ITaskReportingDashboardClient | null = null;

export function getOperationalDaysClient(): IOperationalDaysClient {
  if (operationalDaysClientInstance) return operationalDaysClientInstance;
  operationalDaysClientInstance = isMockedEnvironment() ? new OperationalDaysClientMocked() : new OperationalDaysClientApi();
  return operationalDaysClientInstance;
}

export function getOperationalOverviewClient(): IOperationalOverviewClient {
  if (operationalOverviewClientInstance) return operationalOverviewClientInstance;
  operationalOverviewClientInstance = isMockedEnvironment() ? new OperationalOverviewClientMocked() : new OperationalOverviewClientApi();
  return operationalOverviewClientInstance;
}

export function getTasksClient(): ITasksClient {
  if (tasksClientInstance) return tasksClientInstance;
  tasksClientInstance = isMockedEnvironment() ? new TasksClientMocked() : new TasksClientApi();
  return tasksClientInstance;
}

export function getTaskTemplatesClient(): ITaskTemplatesClient {
  if (taskTemplatesClientInstance) return taskTemplatesClientInstance;
  taskTemplatesClientInstance = isMockedEnvironment() ? new TaskTemplatesClientMocked() : new TaskTemplatesClientApi();
  return taskTemplatesClientInstance;
}

export function getTaskCategoriesClient(): ITaskCategoriesClient {
  if (taskCategoriesClientInstance) return taskCategoriesClientInstance;
  taskCategoriesClientInstance = isMockedEnvironment() ? new TaskCategoriesClientMocked() : new TaskCategoriesClientApi();
  return taskCategoriesClientInstance;
}

export function getTaskStatusesClient(): ITaskStatusesClient {
  if (taskStatusesClientInstance) return taskStatusesClientInstance;
  taskStatusesClientInstance = isMockedEnvironment() ? new TaskStatusesClientMocked() : new TaskStatusesClientApi();
  return taskStatusesClientInstance;
}

export function getTaskPrioritiesClient(): ITaskPrioritiesClient {
  if (taskPrioritiesClientInstance) return taskPrioritiesClientInstance;
  taskPrioritiesClientInstance = isMockedEnvironment() ? new TaskPrioritiesClientMocked() : new TaskPrioritiesClientApi();
  return taskPrioritiesClientInstance;
}

export function getTaskSchedulesClient(): ITaskSchedulesClient {
  if (taskSchedulesClientInstance) return taskSchedulesClientInstance;
  taskSchedulesClientInstance = isMockedEnvironment() ? new TaskSchedulesClientMocked() : new TaskSchedulesClientApi();
  return taskSchedulesClientInstance;
}

export function getTaskReportingDashboardClient(): ITaskReportingDashboardClient {
  if (taskReportingDashboardClientInstance) return taskReportingDashboardClientInstance;
  taskReportingDashboardClientInstance = isMockedEnvironment() ? new TaskReportingDashboardClientMocked() : new TaskReportingDashboardClientApi();
  return taskReportingDashboardClientInstance;
}

/* ---- Pre-configured Client Exports ---- */
/**
 * Pre-configured client instances that automatically use mocked or real clients
 * based on the environment. Use these for simpler imports.
 * 
 * @example
 * import { operationalDaysClient, tasksClient } from 'app/services/api/tasksClient';
 * const days = await operationalDaysClient.getOperationalDays(params);
 */
export const OperationalDaysClient: IOperationalDaysClient = isMockedEnvironment() 
  ? new OperationalDaysClientMocked() 
  : new OperationalDaysClientApi();

export const OperationalOverviewClient: IOperationalOverviewClient = isMockedEnvironment() 
  ? new OperationalOverviewClientMocked() 
  : new OperationalOverviewClientApi();

const _isMocked = isMockedEnvironment();
console.log('[TasksClient] Using mocked client:', _isMocked, 'hostname:', window.location.hostname);
export const TasksClient: ITasksClient = _isMocked
  ? new TasksClientMocked() 
  : new TasksClientApi();

export const TaskTemplatesClient: ITaskTemplatesClient = isMockedEnvironment() 
  ? new TaskTemplatesClientMocked() 
  : new TaskTemplatesClientApi();

export const TaskCategoriesClient: ITaskCategoriesClient = isMockedEnvironment() 
  ? new TaskCategoriesClientMocked() 
  : new TaskCategoriesClientApi();

export const TaskStatusesClient: ITaskStatusesClient = isMockedEnvironment() 
  ? new TaskStatusesClientMocked() 
  : new TaskStatusesClientApi();

export const TaskPrioritiesClient: ITaskPrioritiesClient = isMockedEnvironment() 
  ? new TaskPrioritiesClientMocked() 
  : new TaskPrioritiesClientApi();

export const TaskSchedulesClient: ITaskSchedulesClient = isMockedEnvironment() 
  ? new TaskSchedulesClientMocked() 
  : new TaskSchedulesClientApi();

export const TaskReportingDashboardClient: ITaskReportingDashboardClient = isMockedEnvironment() 
  ? new TaskReportingDashboardClientMocked() 
  : new TaskReportingDashboardClientApi();

/**
 * Reset all client instances (useful for testing).
 */
export function resetTasksClients(): void {
  operationalDaysClientInstance = null;
  operationalOverviewClientInstance = null;
  tasksClientInstance = null;
  taskTemplatesClientInstance = null;
  taskCategoriesClientInstance = null;
  taskStatusesClientInstance = null;
  taskPrioritiesClientInstance = null;
  taskSchedulesClientInstance = null;
  taskReportingDashboardClientInstance = null;
}

/* ---- Backwards Compatibility ---- */
// Export classes directly for legacy code that imports them directly
export {
  OperationalDaysClientApi,
  OperationalOverviewClientApi,
  TasksClientApi,
  TaskTemplatesClientApi,
  TaskCategoriesClientApi,
  TaskStatusesClientApi,
  TaskPrioritiesClientApi,
  TaskSchedulesClientApi,
  TaskReportingDashboardClientApi,
} from './tasksClientApi';

export {
  OperationalDaysClientMocked,
  OperationalOverviewClientMocked,
  TasksClientMocked,
  TaskTemplatesClientMocked,
  TaskCategoriesClientMocked,
  TaskStatusesClientMocked,
  TaskPrioritiesClientMocked,
  TaskSchedulesClientMocked,
  TaskReportingDashboardClientMocked,
} from './tasksClientMocked';

// Export mock store for direct access if needed
export { tasksMockStore } from './tasksMockData';
